import { useEffect, useState } from "react";
import styles from "./Confirmation.module.scss";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { MessageType } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message.types";
import QuoteCard from "./QuoteCard/QuoteCard";
import { useTradeInContext } from "../../../TradeInContext";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import QuoteSummary from "./QuoteSummary/QuoteSummary";
import { QuotesResponse } from "../PaymentOptions/PaymentOptions";
import { removeQuote } from "../../../client/removeQuote";
import { getBasketQuotes } from "./util/getBasketQuotes";
import { HttpResponse } from "../../../client/createEnquiry";
import { TradeInEvents } from "../../../TradeIn";
import { AddToBasketButton } from "./AddToBasket/AddToBasketButton";

export interface TradeInError {
  title: string;
  body: string;
  type: MessageType;
}

const TRADE_IN_UNAVAILABLE_WARNING: TradeInError = {
  title: "Sorry, there's a problem adding your quote",
  body: "Please try again later.",
  type: "error",
};

enum CONFIRMATION_STATE {
  QUOTE_DETAIL,
  QUOTE_SUMMARY,
  EXISTING_QUOTES,
}

const Confirmation = () => {
  const {
    basketApiBaseUrl,
    quoteDetail,
    gradingAnswers,
    selectedDevice,
    existingQuotes,
    setExistingQuotes,
    setCloseModalFlag,
    resetContextData,
  } = useTradeInContext();
  const [basketError, setBasketError] = useState<TradeInError | null>();
  const [lastDeletedQuote, setLastDeletedQuote] = useState<QuotesResponse>();
  const [basketRemoveSuccess, setBasketRemoveSuccess] = useState<
    boolean | null
  >(null);
  const [confirmationState, setConfirmationState] =
    useState<CONFIRMATION_STATE>(
      existingQuotes?.length > 0
        ? CONFIRMATION_STATE.EXISTING_QUOTES
        : CONFIRMATION_STATE.QUOTE_DETAIL,
    );
  const handleBasketError = () => {
    setBasketError(TRADE_IN_UNAVAILABLE_WARNING);
    dispatchMonetateQEvent(TradeInEvents.TRADE_IN_ADD_TO_BASKET);
  };
  const handleBasketSuccess = () => {
    setBasketError(null);
    dispatchMonetateQEvent(TradeInEvents.PW_TRADE_IN_ADDED_QUOTE_TO_BASKET);
    dispatchMonetateQEvent(TradeInEvents.TRADE_IN_ADD_TO_BASKET);
    setConfirmationState(CONFIRMATION_STATE.QUOTE_SUMMARY);
  };

  useEffect(() => {
    if (existingQuotes?.length) {
      setConfirmationState(CONFIRMATION_STATE.EXISTING_QUOTES);
      dispatchMonetateQEvent(TradeInEvents.TRADE_IN_QUOTE_SUMMARY);
    }
  }, [existingQuotes]);

  useEffect(() => {
    window.addEventListener(
      "ADD_TO_BASKET_ESI:WARNING_OR_ERROR",
      handleBasketError,
    );
    window.addEventListener(
      "ADD_TO_BASKET_ESI:SUCCESSFULLY_ADDED",
      handleBasketSuccess,
    );
    return () => {
      window.removeEventListener(
        "ADD_TO_BASKET_ESI:WARNING_OR_ERROR",
        handleBasketError,
      );
      window.removeEventListener(
        "ADD_TO_BASKET_ESI:SUCCESSFULLY_ADDED",
        handleBasketSuccess,
      );
    };
  }, []);

  const onCloseModal = () => {
    setCloseModalFlag(true);
  };
  const raiseBasketQuantityUpdate = () => {
    window.dispatchEvent(
      new CustomEvent("jl-events.browser.basketQuantityUpdated"),
    );
  };

  const handleRemoveQuote = async (quoteId: string) => {
    try {
      const response: HttpResponse = await removeQuote(
        basketApiBaseUrl,
        quoteId,
      );
      if (response.status >= 200 && response.status <= 299) {
        raiseBasketQuantityUpdate();

        setBasketRemoveSuccess(true);
        const existingQuote: QuotesResponse | undefined = existingQuotes.find(
          (quote) => quote.id === quoteId,
        );
        existingQuote && setLastDeletedQuote(existingQuote);
        setExistingQuotes(await getBasketQuotes(basketApiBaseUrl));
      } else {
        setBasketRemoveSuccess(false);
      }
    } catch (e) {
      setBasketRemoveSuccess(false);
    }
  };

  const renderScreen = () => {
    switch (confirmationState) {
      case CONFIRMATION_STATE.QUOTE_DETAIL:
        return (
          quoteDetail && (
            <QuoteCard
              title="Trade In value:"
              payment={quoteDetail.payment}
              deviceName={quoteDetail.deviceName}
              expiry={quoteDetail.expiry}
              ctaButton={
                <AddToBasketButton
                  basketApiBaseUrl={basketApiBaseUrl}
                  tradeInQuoteId={quoteDetail.id}
                />
              }
              gradingQuestions={selectedDevice?.grading}
              gradingAnswers={gradingAnswers}
              showTC={true}
            />
          )
        );
      case CONFIRMATION_STATE.QUOTE_SUMMARY:
        return (
          quoteDetail && (
            <QuoteSummary
              quotes={[quoteDetail]}
              onClose={onCloseModal}
              shouldValidateQuotes={false}
              onRemoveQuote={handleRemoveQuote}
              basketRemoveSuccess={basketRemoveSuccess}
              allowRemove={false}
            />
          )
        );
      default:
        return (
          <QuoteSummary
            quotes={existingQuotes}
            onClose={onCloseModal}
            shouldValidateQuotes={true}
            onRemoveQuote={handleRemoveQuote}
            basketRemoveSuccess={basketRemoveSuccess}
            lastDeletedQuote={lastDeletedQuote}
            onStartNewQuote={resetContextData}
            allowRemove={true}
          />
        );
    }
  };

  function isQuoteDetailState() {
    return confirmationState === CONFIRMATION_STATE.QUOTE_DETAIL;
  }

  return (
    <div className={styles.confirmationDetails}>
      <div className={styles.confirmationDetails__wrapper}>
        {isQuoteDetailState() && (
          <h1 className={styles.confirmationDetails__heading}>
            Review your information
          </h1>
        )}

        {basketError && (
          <DEPRECATED_Message
            title={basketError.title}
            body={basketError.body}
            type={basketError.type}
            className={styles.confirmationDetails__message}
          />
        )}
        {renderScreen()}
      </div>
    </div>
  );
};
export default Confirmation;
